import { memo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  ClipboardList, 
  Settings,
  FileText,
  LogOut
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Logo } from '@/components/ui/logo';
import { ROUTES } from '@/constants/routes';
import { useUIStore } from '@/stores/assessmentStore';

const navigation = [
  { name: 'Dashboard', href: ROUTES.DASHBOARD, icon: LayoutDashboard },
  { name: 'Children', href: ROUTES.CHILDREN, icon: Users },
  { name: 'Assessments', href: ROUTES.ASSESSMENTS, icon: ClipboardList },
  { name: 'Reports', href: ROUTES.REPORTS, icon: FileText },
  { name: 'Settings', href: ROUTES.SETTINGS, icon: Settings },
];

// Memoized navigation item to prevent unnecessary re-renders
const NavItem = memo(function NavItem({ 
  item, 
  isActive 
}: { 
  item: typeof navigation[0]; 
  isActive: boolean;
}) {
  const Icon = item.icon;
  
  return (
    <Link
      to={item.href}
      className={cn(
        'flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200',
        isActive
          ? 'bg-primary/10 text-primary shadow-sm'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
      )}
      aria-current={isActive ? 'page' : undefined}
    >
      <div className={cn(
        'w-9 h-9 rounded-lg flex items-center justify-center transition-colors',
        isActive ? 'bg-primary/10' : 'bg-transparent group-hover:bg-muted'
      )}>
        <Icon 
          className={cn('w-5 h-5', isActive ? 'text-primary' : 'text-muted-foreground')} 
          aria-hidden="true" 
        />
      </div>
      {item.name}
      {isActive && (
        <div className="ml-auto w-1.5 h-1.5 rounded-full bg-primary" />
      )}
    </Link>
  );
});

// User section component
const UserSection = memo(function UserSection() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await signOut();
    navigate(ROUTES.LOGIN);
  };

  const getInitials = () => {
    if (!user?.email) return 'U';
    return user.email.charAt(0).toUpperCase();
  };

  const getDisplayName = () => {
    const metadata = user?.user_metadata as Record<string, string> | undefined;
    if (metadata?.first_name && metadata?.last_name) {
      return `${metadata.first_name} ${metadata.last_name}`;
    }
    return user?.email?.split('@')[0] || 'User';
  };

  return (
    <div className="p-4 border-t border-border space-y-3">
      <div className="flex items-center gap-3 px-3 py-3 rounded-xl bg-muted/50">
        <div 
          className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center text-white font-semibold text-sm shadow-sm"
          aria-hidden="true"
        >
          {getInitials()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground truncate">
            {getDisplayName()}
          </p>
          <p className="text-xs text-muted-foreground truncate">
            Developmental Professional
          </p>
        </div>
      </div>
      <Button 
        variant="outline" 
        className="w-full gap-2 hover:bg-destructive/5 hover:text-destructive hover:border-destructive/20 transition-colors" 
        onClick={handleLogout}
        aria-label="Sign out of your account"
      >
        <LogOut className="w-4 h-4" aria-hidden="true" />
        Sign Out
      </Button>
    </div>
  );
});

// Logo component
const SidebarLogo = memo(function SidebarLogo() {
  return (
    <div className="p-5 border-b border-border">
      <Logo size="md" linkTo={ROUTES.DASHBOARD} />
    </div>
  );
});

// Main Sidebar component
function SidebarComponent() {
  const location = useLocation();
  const { sidebarOpen, toggleSidebar } = useUIStore();
  const pathname = location.pathname;

  return (
    <>
      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden transition-opacity"
          onClick={toggleSidebar}
          aria-hidden="true"
        />
      )}
      
      <aside 
        id="sidebar-navigation"
        className={cn(
          'fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white border-r border-border flex flex-col transition-transform duration-300 ease-out shadow-xl lg:shadow-none',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
        role="navigation"
        aria-label="Main navigation"
      >
        <SidebarLogo />

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {navigation.map((item) => {
            const isActive = pathname === item.href || pathname?.startsWith(`${item.href}/`);
            return (
              <NavItem 
                key={item.name} 
                item={item} 
                isActive={isActive} 
              />
            );
          })}
        </nav>

        <UserSection />
      </aside>
    </>
  );
}

export const Sidebar = memo(SidebarComponent);
